import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import { newsInsights } from '../mock';
import { Button } from '../components/ui/button';

const Article = () => {
  const { slug } = useParams();
  const article = newsInsights.find(item => item.slug === slug);

  if (!article) {
    return (
      <div className="bg-cream text-ink min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl mb-4">Article Not Found</h1>
          <Link to="/insights" className="text-ink/70 hover:text-ink">
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>{article.title} | Valentis Law Group Insights</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://valentislawgroup.com/article/${article.slug}`} />
        <meta property="og:title" content={`${article.title} | Valentis Law Group Insights`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://valentislawgroup.com/article/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={`${article.title} | Valentis Law Group Insights`} />
        <meta name="twitter:description" content={article.excerpt} />
      </Helmet>
      {/* Article Header */}
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link 
            to="/insights"
            className="inline-flex items-center text-sm text-ink/60 hover:text-ink transition-colors duration-300 mb-8"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Insights
          </Link>

          {/* <div className="inline-block px-4 py-2 border border-ink/20 text-xs tracking-widest uppercase mb-6">
            {article.category}
          </div> */}

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex items-center space-x-4 text-sm text-ink/60 mb-8">
            <span className="flex items-center">
              <Calendar size={16} className="mr-2" />
              {article.date}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 lg:py-20 border-t border-ink/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div 
            className="prose prose-lg max-w-none article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-sand-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Interested in Learning More?
          </h2>
          <p className="text-lg text-ink/70 mb-8">
            Contact VALENTIS LAW GROUP to discuss how we can assist with your legal needs.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-ink text-cream hover:bg-ink/90 px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-105"
          >
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </div>
      </section>

      <style>{`
        .article-content p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
          color: rgba(52, 54, 52, 0.85);
        }

        .article-content h2 {
          font-family: 'Merriweather', serif;
          font-size: 2rem;
          margin-top: 3rem;
          margin-bottom: 1.5rem;
          color: #343634;
        }

        .article-content ul {
          margin: 2rem 0;
          padding-left: 0;
          list-style: none;
        }

        .article-content ul li {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
          position: relative;
          color: rgba(52, 54, 52, 0.85);
          line-height: 1.6;
        }

        .article-content ul li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: rgba(52, 54, 52, 0.5);
          font-size: 1.2rem;
        }

        .article-content strong {
          color: #343634;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default Article;
